import type { IngestJobsResponse } from '@cavo-admin/api-interfaces';
import { JobsPageConfig, JobsPageView } from './jobs-page.config';

export function formatElapsed(now: number, created: string): string {
	// I don't love this function, but javascript date formatting has always left something to be desired.
	// I don't want to introduce a dependency just for formatting elapsed time.
	// We have `moment` but the formatting plugin is a bit dusty.
	// Todo - replace this code when a moment successor is chosen.
	// Returns '' when there's nothing to show; JobsComponent renders the placeholder.
	if (!created) return '';

	const createdAt = new Date(created).getTime();
	if (Number.isNaN(createdAt)) return '';

	const seconds = Math.max(0, Math.floor((now - createdAt) / 1000));
	if (seconds < 60) return `${seconds}s`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ${minutes % 60}m`;
	const days = Math.floor(hours / 24);
	return `${days}d ${hours % 24}h`;
}

function toView(response: IngestJobsResponse, now: number): JobsPageView {
	return {
		summary: response.summary,
		rows: response.jobs.map(job => ({
			statusClass: `status-${job.status}`,
			cells: {
				id: String(job.id),
				status: job.status,
				elapsed: formatElapsed(now, job.created),
				documentId: job.data.documentId,
				facility: job.data.facility,
				encounter: job.data.encounter,
				filename: job.data.filename,
				split: job.data.split ? 'Yes' : 'No',
				priority: job.data.priority ?? '',
				tries: String(job.data.tries ?? 0),
				created: job.created,
			},
		})),
	};
}

export const INGEST_JOBS_CONFIG: JobsPageConfig = {
	title: 'Ingest Jobs',
	endpoint: '/api/jobs/ingest',
	emptyMessage: 'No ingest jobs currently in the queue.',
	columns: [
		{ key: 'id', header: 'ID' },
		{ key: 'status', header: 'Status', type: 'status' },
		{ key: 'elapsed', header: 'Elapsed' },
		{ key: 'documentId', header: 'Document ID', type: 'monospace' },
		{ key: 'facility', header: 'Facility' },
		{ key: 'encounter', header: 'Encounter' },
		{ key: 'filename', header: 'Filename' },
		{ key: 'split', header: 'Split' },
		{ key: 'priority', header: 'Priority' },
		{ key: 'tries', header: 'Tries' },
		{ key: 'created', header: 'Created', type: 'date' },
	],
	mapResponse: (response, now) => toView(response as IngestJobsResponse, now),
};
